<template>
  <div>
    <div class="row mb-5">
      <div class="col-12" style="display: flex; align-items: center">
        <span class="display-4">
          {{ `${establishment.company_name}` }} -
          {{ $t("COMMON.PURCHASES_ORDERS") }}
        </span>
        <base-button
          v-if="establishment.active"
          type="primary"
          icon
          size="sm"
          @click="
            () => {
              showSupplierSelectorModal = true;
            }
          "
          :disabled="loading"
        >
          <span class="btn-inner--icon">
            <i class="ni ni-single-copy-04"></i>
            {{ $t("ESTABLISHMENTS.ADD_PURCHASES_ORDER") }}
          </span>
        </base-button>
        <help-button context="purchases-orders" class="ml-2" />
      </div>
    </div>

    <!-- Special Order Reservations List -->
    <div v-if="specialOrderReservations.length > 0" class="mb-5">
      <h4 class="mb-3">{{ $t("SPECIAL_ORDER_RESERVATIONS.TITLE") }}</h4>
      <el-table
        class="align-items-center table-flush"
        header-row-class-name="thead-light"
        :data="specialOrderReservations"
      >
        <div slot="empty" v-if="reservationsLoading">
          <img src="/img/loading.gif" style="height: 100px; width: 100px" />
        </div>
        <el-table-column :label="$t('COMMON.SUPPLIER')" min-width="200px">
          <template slot-scope="{ row }">
            <span v-if="row.supplier">
              {{ row.supplier.company_name }}
            </span>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('COMMON.PRODUCT')" min-width="250px">
          <template slot-scope="{ row }">
            <span v-if="row.product">
              {{ row.product.code }} - {{ row.product.name }}
            </span>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('SPECIAL_ORDER_RESERVATIONS.REMAINING_QUANTITY')"
          min-width="150px"
        >
          <template slot-scope="{ row }">
            {{ row.remaining_quantity }}
          </template>
        </el-table-column>
        <el-table-column min-width="80px" align="center" fixed="right">
          <template slot-scope="{ row }">
            <el-tooltip :content="$t('COMMON.DELETE')" placement="top">
              <a
                type="text"
                @click="deleteReservation(row.id)"
                class="table-action table-action-delete"
                data-toggle="tooltip"
                style="cursor: pointer"
              >
                <i class="fas fa-trash"></i>
              </a>
            </el-tooltip>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <purchases-order-list-table
      v-if="establishment.id"
      :filterEstablishment="establishment.id"
      :hideUpdateAndDelete="true"
    />

    <modal
      :show="showSupplierSelectorModal"
      modal-classes="modal-secondary"
      size="lg"
    >
      <form class="" @submit.prevent="">
        <div>
          <base-input :label="`${$t(`PURCHASES_ORDERS.SELECT_SUPPLIERS`)} (*)`">
            <supplier-selector
              :allowNone="true"
              :filterable="true"
              :showAll="false"
              :filterOrganization="establishment.organization.id"
              @supplierChanged="
                (supplierId) => {
                  supplier.id = supplierId;
                }
              "
            />
          </base-input>
        </div>
      </form>

      <template slot="footer">
        <button
          type="submit"
          class="btn btn-primary"
          :disabled="!supplier.id"
          @click="
            () => {
              addPurchasesOrder();
            }
          "
        >
          {{ $t("ESTABLISHMENTS.ADD_PURCHASES_ORDER") }}
        </button>

        <button
          type="button"
          class="btn btn-link ml-auto"
          @click="
            () => {
              showSupplierSelectorModal = false;
            }
          "
        >
          {{ $t("COMMON.CLOSE") }}
        </button>
      </template>
    </modal>
  </div>
</template>

<script>
import moment from "moment";
import swal from "sweetalert2";
import { cloneDeep } from "lodash";
import { mapGetters } from "vuex";
import { Table, TableColumn, Tooltip } from "element-ui";
import SupplierSelector from "@/components/SupplierSelector.vue";
import defaultPurchasesOrder from "../../../PurchasesModule/PurchasesOrderManagement/defaultPurchasesOrder";
import PurchasesOrderListTable from "../../../PurchasesModule/PurchasesOrderManagement/partials/PurchasesOrderListTable.vue";

export default {
  name: "establishment-view-orders",

  components: {
    PurchasesOrderListTable,
    SupplierSelector,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Tooltip.name]: Tooltip,
  },

  mixins: [],

  props: ["establishment"],

  data() {
    return {
      showSupplierSelectorModal: false,
      supplier: {
        type: "suppliers",
        id: null,
      },
      loading: false,
      specialOrderReservations: [],
      reservationsLoading: false,
    };
  },

  computed: {
    ...mapGetters({
      organizationConfig: "organizationOptions/organizationConfig",
    }),
  },

  created() {
    this.loadSpecialOrderReservations();
  },

  methods: {
    async loadSpecialOrderReservations() {
      if (!this.establishment?.id) return;

      this.reservationsLoading = true;
      try {
        const params = {
          filter: {
            establishment: this.establishment.id,
          },
          include: "supplier,product",
        };
        await this.$store.dispatch("specialOrderReservations/list", params);
        this.specialOrderReservations =
          this.$store.getters["specialOrderReservations/list"];
      } catch (error) {
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      } finally {
        this.reservationsLoading = false;
      }
    },

    async deleteReservation(id) {
      const confirmation = await swal.fire({
        title: this.$t("SPECIAL_ORDER_RESERVATIONS.DELETE_CONFIRMATION"),
        type: "question",
        buttonsStyling: false,
        showCancelButton: true,
        confirmButtonText: this.$t("COMMON.YES"),
        cancelButtonText: this.$t("COMMON.NO"),
        confirmButtonClass: "btn btn-primary",
        cancelButtonClass: "btn btn-warning",
      });

      if (confirmation.value === true) {
        try {
          await this.$store.dispatch("specialOrderReservations/destroy", id);
          this.$notify({
            type: "success",
            message: this.$t("SPECIAL_ORDER_RESERVATIONS.DELETED"),
          });
          await this.loadSpecialOrderReservations();
        } catch (error) {
          this.$notify({
            type: "danger",
            message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
          });
        }
      }
    },

    async addPurchasesOrder() {
      swal.fire({
        allowEscapeKey: false,
        allowOutsideClick: false,
        onOpen: () => {
          swal.showLoading();
        },
      });
      await this.$store.dispatch(
        "organizationOptions/getOrganizationConfig",
        this.establishment.organization.id
      );

      this.loading = true;
      const purchasesOrderData = cloneDeep(defaultPurchasesOrder);
      delete purchasesOrderData.purchasesInvoice;
      purchasesOrderData.expiration_time = moment(
        purchasesOrderData.expiration_time
      ).toISOString();

      purchasesOrderData.issuer = this.supplier;
      purchasesOrderData.organization = this.establishment.organization;
      purchasesOrderData.allowedLocations = this.establishment.allowedLocations;
      purchasesOrderData.billing_entity_type =
        this.establishment.billing_entity_type;
      purchasesOrderData.billing_company_name =
        this.establishment.billing_company_name;
      purchasesOrderData.billing_firstname =
        this.establishment.billing_firstname;
      purchasesOrderData.billing_lastname = this.establishment.billing_lastname;
      purchasesOrderData.billing_country = this.establishment.billing_country;
      purchasesOrderData.billing_state = this.establishment.billing_state;
      purchasesOrderData.billing_city = this.establishment.billing_city;
      purchasesOrderData.billing_zipcode = this.establishment.billing_zipcode;
      purchasesOrderData.billing_address = this.establishment.billing_address;
      purchasesOrderData.billing_email = this.establishment.billing_email;
      purchasesOrderData.terms_conditions =
        this.organizationConfig.DEFAULT_PURCHASES_ORDER_TERMS;
      purchasesOrderData.description =
        this.organizationConfig.DEFAULT_PURCHASES_ORDER_DESCRIPTION;
      purchasesOrderData.paymentTerm = {
        type: "payment-terms",
        id: this.organizationConfig.DEFAULT_PURCHASES_PAYMENT_TERMS,
      };

      delete purchasesOrderData.cafeteria;
      purchasesOrderData.establishment = {
        type: "establishments",
        id: this.establishment.id,
      };
      purchasesOrderData.serviceCenter = {
        type: "service-centers",
        id: this.establishment.serviceCenter.id,
      };

      if (this.establishment.warehouse && this.establishment.warehouse.id) {
        purchasesOrderData.destinationWarehouse = {
          type: "warehouses",
          id: this.establishment.warehouse.id,
        };
      } else {
        delete purchasesOrderData.destinationWarehouse;
      }

      try {
        await this.$store.dispatch("purchasesOrders/add", purchasesOrderData);
        this.$notify({
          type: "success",
          message: this.$t("PURCHASES_ORDERS.PURCHASES_ORDER_ADDED"),
        });
        const purchasesOrder = await this.$store.getters[
          "purchasesOrders/purchasesOrder"
        ];
        this.$router.push({
          name: "View PurchasesOrder",
          params: { id: purchasesOrder.id },
        });
        this.loading = false;
      } catch (error) {
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
        this.loading = false;
      }
      swal.close();
    },
  },

  mounted() {},

  watch: {
    establishment(establishment) {
      if (establishment?.id) {
        this.loadSpecialOrderReservations();
      }
    },
  },
};
</script>
